import { useState } from 'react';
import useSWR from 'swr';
import { adminApi } from '../../api/admin';
import type { Host, EnvName } from '../../api/types';

const EMPTY: Host = { id: '', name: '', env: 'dev', role: null, probe_url: null };

export function HostsTab() {
  const { data: hosts = [], mutate } = useSWR('admin/hosts', () => adminApi.listHosts());
  const [editing, setEditing] = useState<Host | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(h: Host) {
    setErr(null);
    try {
      if (adding) await adminApi.createHost(h);
      else await adminApi.updateHost(h);
      await mutate();
      setEditing(null);
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(h: Host) {
    if (!confirm(`Delete host '${h.id}' and ALL services attached to it?`)) return;
    setErr(null);
    try {
      await adminApi.deleteHost(h.id);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
          Hosts ({hosts.length})
        </h3>
        <button
          type="button"
          className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
          onClick={() => {
            setAdding(true);
            setEditing({ ...EMPTY });
          }}
        >
          + Add host
        </button>
      </div>
      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Env</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Probe URL</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {hosts.map((h) => (
              <tr key={h.id} className="border-t border-cockpit-border">
                <td className="px-3 py-2 font-mono text-xs">{h.id}</td>
                <td className="px-3 py-2">{h.name}</td>
                <td className="px-3 py-2 text-xs uppercase">{h.env}</td>
                <td className="px-3 py-2 text-xs">{h.role ?? '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {h.probe_url ?? <span className="text-cockpit-dim">local</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text"
                    onClick={() => {
                      setAdding(false);
                      setEditing(h);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs text-cockpit-crit hover:underline"
                    onClick={() => void remove(h)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {hosts.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no hosts yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <HostForm
          host={editing}
          isNew={adding}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function HostForm({
  host,
  isNew,
  onSave,
  onCancel,
}: {
  host: Host;
  isNew: boolean;
  onSave: (h: Host) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(host);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="space-y-3 rounded border border-cockpit-border bg-cockpit-panel p-4"
    >
      <h4 className="text-sm font-semibold">{isNew ? 'New host' : `Edit ${host.id}`}</h4>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Field label="ID (immutable)" htmlFor="id">
          <input
            id="id"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            placeholder="t2-ws2025-dev"
            required
          />
        </Field>
        <Field label="Display name" htmlFor="name">
          <input
            id="name"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="T2 Dev VM"
            required
          />
        </Field>
        <Field label="Environment" htmlFor="env">
          <select
            id="env"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.env}
            onChange={(e) => setForm({ ...form, env: e.target.value as EnvName })}
          >
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
        </Field>
        <Field label="Role" htmlFor="role">
          <input
            id="role"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.role ?? ''}
            onChange={(e) => setForm({ ...form, role: e.target.value || null })}
            placeholder="app-server, pulse-home"
          />
        </Field>
        <Field label="Probe URL (leave blank for local host)" htmlFor="probe_url" colSpan={2}>
          <input
            id="probe_url"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.probe_url ?? ''}
            onChange={(e) => setForm({ ...form, probe_url: e.target.value || null })}
            placeholder="https://staging-probe.insightdelivered.com"
          />
        </Field>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg">
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-cockpit-border px-3 py-1 text-xs text-cockpit-dim hover:text-cockpit-text"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
  colSpan = 1,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  colSpan?: 1 | 2;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={colSpan === 2 ? 'col-span-2 flex flex-col gap-1' : 'flex flex-col gap-1'}
    >
      <span className="text-cockpit-dim">{label}</span>
      {children}
    </label>
  );
}
